import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { agentExecutionApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import type { SessionResponse } from "src/clients/agent-management";

const PREFIX_CACHE_KEY = "agent-execution";

export const useAgentExecutionClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  return useMemo(
    () =>
      workspace?.base_url ? agentExecutionApi(workspace.base_url) : undefined,
    [workspace?.base_url],
  );
};

export const useAgentSession = (sessionId: string | null) => {
  const client = useAgentExecutionClient();

  return useQuery({
    queryKey: [PREFIX_CACHE_KEY, "sessions", sessionId],
    queryFn: async (): Promise<SessionResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      if (!sessionId) {
        throw new Error("Session ID is required");
      }
      const response =
        await client.getSessionAgentsManagementApiV0SessionsSessionIdGet({
          sessionId,
          includeMessages: true,
        });
      return response.data;
    },
    enabled: !!client && !!sessionId,
    refetchInterval: (query) => {
      // Stop polling when session is complete
      const data = query.state.data;
      if (
        data?.status === "success" ||
        data?.status === "failed" ||
        data?.status === "cancelled"
      ) {
        return false;
      }
      // Poll every 1 second while running
      return 1000;
    },
  });
};
